import { format } from "date-fns"
import { toZonedTime } from "date-fns-tz"

const dayKey = (d) => format(d, "yyyy-MM-dd")

// A stored rate's day (UTC midnight, as epoch ms or seconds), read as that local day - the
// way getExchangeRates keys them
const rateDayKey = (raw) => {
    if (raw === undefined || raw === null) return null
    const n = Number(raw)
    const date = Number.isNaN(n) ? new Date(raw) : new Date(n < 1e11 ? n * 1000 : n)
    return Number.isNaN(date.getTime()) ? null : dayKey(toZonedTime(date))
}

/**
 * Exchange rates by day, read the way the server's financials read them (api
 * financials.fx.js): a day takes the latest rate published on or before it. So weekends,
 * holidays, a missed run and today (published tonight) all take the last rate there is, and
 * a day before the first rate held takes the first one after it.
 *
 * @param rates {object[]} getExchangeRates' days: [{ day, rates: { [currency]: units per 1 base } }]
 * @return {(currency: string, day: Date|number) => number|null} the rate (divide by it to
 *   convert into the base), or null when there's none for that currency at all
 */
export function makeRateLookup(rates = []) {
    const series = new Map()

    const seriesOf = (currency) => {
        if (!series.has(currency)) {
            const list = []
            for (const r of rates || []) {
                const rate = Number(r?.rates?.[currency])
                const key = r && rateDayKey(r.day)
                if (key && rate > 0) list.push([key, rate])
            }
            list.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
            series.set(currency, list)
        }
        return series.get(currency)
    }

    return (currency, day) => {
        const list = seriesOf(currency)
        const date = day instanceof Date ? day : new Date(day)
        if (!list.length || Number.isNaN(date.getTime())) return null

        const target = dayKey(date)
        let lo = 0
        let hi = list.length - 1
        let found = -1
        while (lo <= hi) {
            const mid = (lo + hi) >> 1
            if (list[mid][0] <= target) {
                found = mid
                lo = mid + 1
            } else {
                hi = mid - 1
            }
        }

        return list[found >= 0 ? found : 0][1]
    }
}
